/**
 * TorneoDetalle.js — Vista principal de un torneo activo.
 * Incluye: tabla de posiciones, tablero, dado, comodines y modales de detalle.
 *
 * Props:
 *   session  {object}  - Sesión de Supabase
 *   esAdmin  {boolean} - Si el usuario puede acceder al panel admin
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import Tablero from '../../Tablero/Tablero';
import { fetchComodines } from '../../Tablero/tableroLogica';
import ModalVerEquipo from '../../ModalVerEquipo/ModalVerEquipo';
import RankingTorneo from './RankingTorneo';
import Tutorial from '../../../components/Tutorial/Tutorial';
import { TUTORIALES } from '../../../constants/tutorialesConfig';

// ── Constantes ────────────────────────────────────────────────────────────────

const COLORES_FICHAS = ['#00f2ff', '#a855f7', '#f59e0b', '#22c55e', '#ef4444', '#ec4899', '#3b82f6', '#84cc16'];

/** Pool de restricciones para el comodín "restricción extra" */
const POOL_RESTRICCIONES = [
'Al menos 3 zurdos',
  'Mínimo 1 Sub-23',
  'Mínimo 2 jugadores con +33 años',
  'Máximo 2 jugadores con +33 años',
  'Máximo 4 jugadores de más de $6.5M',
  'Máximo 2 jugadores de la última final del mundial',
  'Mínimo 2 ex Serie A',
  'Mínimo 2 ex Premier League',
  'Mínimo 2 ex La Liga',
  'Mínimo 2 ex Ligue 1',
  'Mínimo 2 ex Bundesliga',
  'Mínimo 1 ex Eredivisie',
  'Mínimo 2 Serie A actual',
  'Mínimo 2 La Liga actual',
  'Mínimo 2 Ligue 1 actual',
  'Mínimo 2 Bundesliga actual',
  'Mínimo 1 Eredivisie actual',
  'Mínimo 3 jugadores Premier actual',
  'Mínimo 2 defensores sudamericanos',
  'Mínimo 2 defensores europeos',
  'Mínimo 1 defensor asiático',
  'Mínimo 1 defensor africano',
  'Mínimo 1 defensor de Oceanía',
  'Máximo 1 defensor europeo',
  'Máximo 1 defensor sudamericano',
  'Mínimo 1 delantero asiático',
  'Mínimo 2 delanteros africanos',
  'Máximo 6 jugadores de selecciones campeonas del mundo',
  'Máximo 4 europeos',
  'Mínimo 2 de América',
  'Mínimo 3 de la misma selección',
  'Mínimo 1 arquero zurdo',
  'Mínimo 1 jugador de las 3 peores selecciones del ranking FIFA',
  'Ningún jugador de las 3 mejores selecciones del ranking FIFA',
  'Mínimo 3 jugadores con altura +1.82',
  'Mínimo 3 jugadores con altura +1.80',
];

const restriccionAleatoria = () => POOL_RESTRICCIONES[Math.floor(Math.random() * POOL_RESTRICCIONES.length)];

// ── Helpers de cálculo ────────────────────────────────────────────────────────

/** Extrae jugadores relevantes de un equipo para una restricción dada */
const calcularEdad = (fecha) => {
  if (!fecha) return null;
  const hoy = new Date(), nac = new Date(fecha);
  let edad = hoy.getFullYear() - nac.getFullYear();
  if (hoy.getMonth() < nac.getMonth() || (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate())) edad--;
  return edad;
};

// ── Gentilicios — sincronizado con App.js ──
const SUDAMERICANOS   = ['Argentina','Brasil','Uruguay','Colombia','Chile','Paraguay','Venezuela','Bolivia','Ecuador','Perú'];
const AFRICANOS       = ['Marruecos','Senegal','Mali','Sudáfrica','Túnez','Ghana','Nigeria','RD Congo','Camerún','Egipto','Costa de Marfil','Argelia','Cabo Verde'];
const ASIATICOS       = ['Japón','Corea del Sur','Irán','Arabia Saudita','Qatar','Australia','Uzbekistán','Jordania','Irak'];
const EUROPEOS        = ['España','Francia','Alemania','Inglaterra','Portugal','Países Bajos','Bélgica','Croacia','Serbia','Dinamarca','Suecia','Noruega','Austria','Suiza','Turquía','Escocia','República Checa','Polonia','Ucrania','Rumanía','Hungría','Eslovenia','Albania','Georgia','Bosnia','Italia'];
const CONCACAF        = ['México','USA','Canadá','Panamá','Jamaica','Honduras','Costa Rica','Guatemala','Cuba','Haití','Curazao'];
const OCEANIA         = ['Australia','Nueva Zelanda'];
const CAMPEONES_MUNDO = ['Argentina','Brasil','Uruguay','España','Francia','Inglaterra','Italia','Alemania'];

const getJugadoresRelevantes = (equipo, restriccionTexto) => {
  const titulares = Object.entries(equipo || {})
    .filter(([k]) => Number(k) <= 10)
    .map(([, j]) => j).filter(Boolean);
  const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const t = norm(restriccionTexto);

  // ── Posición + continente (específicos primero) ──
  if (t.includes('defensor') && t.includes('africano'))    return titulares.filter(j => j.posicion?.sigla === 'DEF' && AFRICANOS.includes(j.nacionalidad));
  if (t.includes('defensor') && (t.includes('europeo') || t.includes('europe'))) return titulares.filter(j => j.posicion?.sigla === 'DEF' && EUROPEOS.includes(j.nacionalidad));
  if (t.includes('defensor') && t.includes('sudamericano'))return titulares.filter(j => j.posicion?.sigla === 'DEF' && SUDAMERICANOS.includes(j.nacionalidad));
  if (t.includes('defensor') && (t.includes('asiatico') || t.includes('asiático'))) return titulares.filter(j => j.posicion?.sigla === 'DEF' && ASIATICOS.includes(j.nacionalidad));
  if (t.includes('defensor') && t.includes('oceania'))     return titulares.filter(j => j.posicion?.sigla === 'DEF' && OCEANIA.includes(j.nacionalidad));
  if (t.includes('delantero') && t.includes('africano'))   return titulares.filter(j => j.posicion?.sigla === 'DEL' && AFRICANOS.includes(j.nacionalidad));
  if (t.includes('delantero') && (t.includes('europeo') || t.includes('europe'))) return titulares.filter(j => j.posicion?.sigla === 'DEL' && EUROPEOS.includes(j.nacionalidad));
  if (t.includes('delantero') && t.includes('sudamericano'))return titulares.filter(j => j.posicion?.sigla === 'DEL' && SUDAMERICANOS.includes(j.nacionalidad));
  if (t.includes('delantero') && (t.includes('asiatico') || t.includes('asiático'))) return titulares.filter(j => j.posicion?.sigla === 'DEL' && ASIATICOS.includes(j.nacionalidad));

  // ── Ex ligas (ANTES que ligas actuales) ──
  if (t.includes('ex serie a'))    return titulares.filter(j => j.ex_ligas?.includes('Serie A'));
  if (t.includes('ex premier'))    return titulares.filter(j => j.ex_ligas?.includes('Premier League'));
  if (t.includes('ex la liga'))    return titulares.filter(j => j.ex_ligas?.includes('La Liga'));
  if (t.includes('ex ligue'))      return titulares.filter(j => j.ex_ligas?.includes('Ligue 1'));
  if (t.includes('ex bundesliga')) return titulares.filter(j => j.ex_ligas?.includes('Bundesliga'));
  if (t.includes('ex eredivisie')) return titulares.filter(j => j.ex_ligas?.includes('Eredivisie'));

  // ── Ligas actuales ──
  if (t.includes('serie a') && !t.includes('ex'))    return titulares.filter(j => j.liga_club === 'Serie A');
  if (t.includes('premier') && !t.includes('ex'))    return titulares.filter(j => j.liga_club === 'Premier League');
  if (t.includes('la liga') && !t.includes('ex'))    return titulares.filter(j => j.liga_club === 'La Liga');
  if (t.includes('ligue') && !t.includes('ex'))      return titulares.filter(j => j.liga_club === 'Ligue 1');
  if (t.includes('bundesliga') && !t.includes('ex')) return titulares.filter(j => j.liga_club === 'Bundesliga');
  if (t.includes('eredivisie') && !t.includes('ex')) return titulares.filter(j => j.liga_club === 'Eredivisie');

  // ── Continentes genéricos ──
  if (t.includes('africano') || t.includes('africa')) return titulares.filter(j => AFRICANOS.includes(j.nacionalidad));
  if (t.includes('sudamericano'))                     return titulares.filter(j => SUDAMERICANOS.includes(j.nacionalidad));
  if (t.includes('asiatico') || t.includes('asiático') || t.includes('asia')) return titulares.filter(j => ASIATICOS.includes(j.nacionalidad));
  if (t.includes('europeo') || t.includes('europe'))  return titulares.filter(j => EUROPEOS.includes(j.nacionalidad));
  if (t.includes('oceania'))                          return titulares.filter(j => OCEANIA.includes(j.nacionalidad));
  if (t.includes('concacaf') || t.includes('centroameric') || t.includes('norteameric')) return titulares.filter(j => CONCACAF.includes(j.nacionalidad));
  if (t.includes('de america') || t.includes('de am')) return titulares.filter(j => [...SUDAMERICANOS, ...CONCACAF].includes(j.nacionalidad));

  // ── Campeonas / finalistas ──
  if (t.includes('campeonas') || (t.includes('campe') && t.includes('mundo'))) return titulares.filter(j => CAMPEONES_MUNDO.includes(j.nacionalidad));
  if (t.includes('ultima final') || t.includes('ltima final'))                  return titulares.filter(j => ['Argentina','Francia'].includes(j.nacionalidad));

  // ── Ranking FIFA ──
  if (t.includes('3 mejores') || t.includes('mejores selecciones')) return titulares.filter(j => ['Francia','España','Argentina'].includes(j.nacionalidad));
  if (t.includes('3 peores') || t.includes('peores selecciones'))   return titulares.filter(j => ['Nueva Zelanda','Haití','Curazao'].includes(j.nacionalidad));

  // ── Zurdos ──
  if (t.includes('zurdo')) return titulares.filter(j => j.pie_habil === 'Izquierdo');

  // ── Extranjeros / locales ──
  if (t.includes('extranjero')) return titulares.filter(j => j.nacionalidad !== 'Argentina');
  if (t.includes('local'))      return titulares.filter(j => j.nacionalidad === 'Argentina');

  // ── Clubs ──
  if (t.includes('river') || t.includes('riv')) return titulares.filter(j => j.club?.sigla === 'RIV');
  if (t.includes('boca')  || t.includes('boc')) return titulares.filter(j => j.club?.sigla === 'BOC');
  if (t.includes('top 5') || t.includes('grandes')) return titulares.filter(j => ['RIV','BOC','IND','SAN','RAC'].includes(j.club?.sigla));

  // ── Precio ──
  if (t.includes('6.5') || t.includes('6,5')) return titulares.filter(j => j.precio > 6_500_000);
  if (t.includes('precio') || t.includes('max $')) {
    const precMatch = t.match(/\$?(\d+(?:[.,]\d+)?)\s*m/i);
    const precMax = precMatch ? parseFloat(precMatch[1].replace(',','.')) * 1_000_000 : 5_500_000;
    return titulares.filter(j => j.precio > precMax);
  }

  // ── Edad ──
  if (t.includes('+33') || (t.includes('33') && t.includes('ano'))) return titulares.filter(j => { const e = calcularEdad(j.fecha_nacimiento); return e !== null && e >= 33; });
  if (t.includes('+35') || (t.includes('35') && t.includes('ano'))) return titulares.filter(j => { const e = calcularEdad(j.fecha_nacimiento); return e !== null && e >= 35; });
  if (t.includes('+36') || (t.includes('36') && t.includes('ano'))) return titulares.filter(j => { const e = calcularEdad(j.fecha_nacimiento); return e !== null && e >= 36; });
  if (t.includes('sub-23') || t.includes('sub23')) return titulares.filter(j => { const e = calcularEdad(j.fecha_nacimiento); return e !== null && e <= 23; });
  if (t.includes('sub-21') || t.includes('sub21')) return titulares.filter(j => { const e = calcularEdad(j.fecha_nacimiento); return e !== null && e <= 21; });

  // ── Altura ──
  if (t.includes('altura') || t.includes('1.80') || t.includes('1.82') || t.includes('+1.8')) {
    const altMatch = t.match(/1[,.](\d{2})/);
    const altMin = altMatch ? parseFloat('1.' + altMatch[1]) * 100 : 182;
    return titulares.filter(j => j.altura != null && Number(j.altura) >= altMin);
  }

  // ── Misma selección ──
  if (t.includes('misma selecci')) {
    const porSel = {};
    titulares.forEach(j => { const s = j.seleccion || j.nacionalidad || 'SC'; porSel[s] = (porSel[s] || 0) + 1; });
    const maxSel = Math.max(...Object.values(porSel));
    if (maxSel >= 3) return titulares.filter(j => (porSel[j.seleccion || j.nacionalidad || 'SC'] || 0) >= 3);
    return [];
  }

  // ── Mismo club ──
  if (t.includes('mismo club')) {
    const porClub = {};
    titulares.forEach(j => { const s = j.club?.sigla || 'SC'; porClub[s] = (porClub[s] || 0) + 1; });
    return titulares.filter(j => (porClub[j.club?.sigla || 'SC'] || 0) > 1);
  }

  // ── Dorsales ──
  if (t.includes('dorsal') && t.includes('10')) return titulares.filter(j => Number(j.dorsal) === 10);
  if (t.includes('dorsal') && t.includes(' 9') && !t.includes('11')) return titulares.filter(j => Number(j.dorsal) === 9);
  if (t.includes('dorsal') && t.includes(' 7')) return titulares.filter(j => Number(j.dorsal) === 7);
  if (t.includes('dorsal') && t.includes('11')) return titulares.filter(j => Number(j.dorsal) === 11);

  return [];
};


// ── Componente ────────────────────────────────────────────────────────────────

const TorneoDetalle = ({ session, esAdmin }) => {
  const { id }   = useParams();
  const navigate = useNavigate();

  // Datos del torneo
  const [torneo,              setTorneo]              = useState(null);
  const [participantes,       setParticipantes]       = useState([]);
  const [fechas,              setFechas]              = useState([]);
  const [puntos,              setPuntos]              = useState([]);
  const [configCasilleros,    setConfigCasilleros]    = useState({});
  const [draftsParticipantes, setDraftsParticipantes] = useState({});
  const [loading,             setLoading]             = useState(true);

  // UI / navegación
  const [fechaVista, setFechaVista] = useState('general');

  // Dado
  const [dadoValor,    setDadoValor]    = useState(null);
  const [dadoAnimando, setDadoAnimando] = useState(false);
  const [dadoTirado,   setDadoTirado]   = useState(false);

  // Movimiento pendiente: esperando click del usuario en el casillero final
  const [pendienteMovimiento, setPendienteMovimiento] = useState(null);
  // { resultado, pathCasilleros, casilleroFinal, fechaActiva }
  const [movimientoEnCurso, setMovimientoEnCurso] = useState(false);

  // Comodines
  const [comodines,          setComodines]          = useState([]);
  const [menuComodines,      setMenuComodines]      = useState(false);
  const [comodinActivo,      setComodinActivo]      = useState(null); // comodín esperando rival
  const [comodinCambioRestr, setComodinCambioRestr] = useState(null); // modal cambiar restricción
  const [previewCambio,      setPreviewCambio]      = useState(null);

  // Modales
  const [equipoRival,  setEquipoRival]  = useState(null);
  const [equipoDetalle, setEquipoDetalle] = useState(null);
  const [toast,        setToast]         = useState(null);
  const [cargandoComodin, setCargandoComodin] = useState(false);
  const [efectosMovimiento, setEfectosMovimiento] = useState(null); // resultado del tablero

  // Tags de clan — estado separado para no tocar la lógica del tablero
  const [clanTags, setClanTags] = useState({}); // { user_id: 'TAG' }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTodo(); }, [id]);

  // Cargar clan_tags de los participantes sin tocar el fetchTodo
  useEffect(() => {
    if (!participantes.length) return;
    const ids = participantes.map(p => p.user_id);
    supabase.from('perfiles').select('id, clan_tag').in('id', ids)
      .then(({ data }) => {
        if (!data) return;
        const mapa = {};
        data.forEach(p => { if (p.clan_tag) mapa[p.id] = p.clan_tag; });
        setClanTags(mapa);
      });
  }, [participantes]);

  const mostrarToast = (msg, tipo = 'ok') => {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Carga de datos ────────────────────────────────────────────────────────

  const fetchTodo = async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    const [{ data: t }, { data: p }, { data: f }, { data: pts }, { data: cfg }] = await Promise.all([
      supabase.from('torneos').select('*').eq('id', id).single(),
      supabase.from('torneo_participantes').select('*').eq('torneo_id', id),
      supabase.from('torneo_fechas').select('*').eq('torneo_id', id).order('numero_fecha'),
      supabase.from('torneo_puntos').select('*, capitan_index, fecha:torneo_fechas(numero_fecha)').eq('torneo_id', id),
      supabase.from('casillero_config').select('*').order('casillero'),
    ]);

    setTorneo(t);
    setParticipantes(p || []);

    // Mostrar fechas cerradas + activa + las que tienen puntos cargados (por si el admin
    // distribuyó sin cerrar formalmente la fecha)
    const fechasConPuntos = new Set((pts || []).map(p => p.fecha_id));
    const fechasVisibles = (f || []).filter(x => x.cerrada || x.activa || fechasConPuntos.has(x.id));
    setFechas(fechasVisibles);

    setPuntos(pts || []);
    const configDedup = {};
    (cfg || []).forEach(c => { if (!configDedup[c.casillero]) configDedup[c.casillero] = c; });
    setConfigCasilleros(configDedup);

    // Arrancar parado en la fecha activa (o la última cerrada si no hay activa)
    const fechaActiva = fechasVisibles.find(x => x.activa && !x.cerrada);
    const fechaDefault = fechaActiva || fechasVisibles[fechasVisibles.length - 1];
    if (fechaDefault) setFechaVista(fechaDefault.id);

    // Estado del dado para la fecha activa
    const fechaActivaNum = f?.find(x => x.activa && !x.cerrada)?.numero_fecha || 0;
    const miParticipante = p?.find(x => x.user_id === session.user.id);
    if (miParticipante?.dado_tirado_fecha === fechaActivaNum && fechaActivaNum > 0) {
      setDadoTirado(true);
    }

    // Drafts de todos los participantes filtrados por jornada activa
    if (p?.length) {
      const jornadaNum = f?.find(x => x.activa && !x.cerrada)?.numero_fecha || 1;
      const { data: drafts } = await supabase
        .from('drafts')
         .select('user_id, restricciones, equipo, capitan_index, confirmado, jornada, formacion, presupuesto')
        .in('user_id', p.map(x => x.user_id))
        .eq('jornada', jornadaNum);
      setDraftsParticipantes(Object.fromEntries((drafts || []).map(d => [d.user_id, d])));
    }

    try {
      setComodines(await fetchComodines(id, session.user.id));
    } catch { setComodines([]); }

    setLoading(false);
  };

  // ── Tabla de posiciones ───────────────────────────────────────────────────

  const calcularTabla = () => {
    return participantes.map(p => {
      let puntosTotal = 0, presupuestoGastado = 0;

      if (fechaVista === 'general') {
        const misPuntos = puntos.filter(pt => pt.user_id === p.user_id);
        puntosTotal = misPuntos.reduce((acc, pt) => acc + (pt.puntos || 0), 0);
        const conPresp = misPuntos.filter(pt => pt.presupuesto_gastado > 0);
        presupuestoGastado = conPresp.length
          ? Math.round(conPresp.reduce((acc, pt) => acc + pt.presupuesto_gastado, 0) / conPresp.length)
          : 0;
      } else {
        const ptFecha = puntos.find(pt => pt.user_id === p.user_id && pt.fecha_id === fechaVista);
        puntosTotal        = ptFecha?.puntos              || 0;
        presupuestoGastado = ptFecha?.presupuesto_gastado || 0;
      }

      const fechasGanadas = fechas.filter(f => {
        const ptsEnFecha = puntos.filter(pt => pt.fecha_id === f.id);
        if (!ptsEnFecha.length) return false;
        const ganador = ptsEnFecha.reduce((mejor, pt) => {
          if (!mejor) return pt;
          if (pt.puntos > mejor.puntos) return pt;
          if (pt.puntos === mejor.puntos && (pt.presupuesto_gastado || 0) < (mejor.presupuesto_gastado || 0)) return pt;
          return mejor;
        }, null);
        return ganador?.user_id === p.user_id && ganador?.puntos > 0;
      }).length;

      return { ...p, puntosTotal, fechasGanadas, presupuestoGastado };
    }).sort((a, b) => b.puntosTotal !== a.puntosTotal
      ? b.puntosTotal - a.puntosTotal
      : a.presupuestoGastado - b.presupuestoGastado
    );
  };

  // ── Comodines ─────────────────────────────────────────────────────────────

  const ejecutarComodin = async (comodin, rivalId = null) => {
    const tipo           = comodin.tipo_id;
    const necesitaRival  = ['sabotaje', 'sabotaje_plus', 'sabotaje_plusplus', 'ver_rival', 'restriccion_extra_lider'].includes(tipo);

    if (necesitaRival && !rivalId) { setComodinActivo(comodin); return; }

    setCargandoComodin(true);

    try {
      // Validar que el rival no haya confirmado (excepto ver_rival que funciona siempre)
      if (rivalId && tipo !== 'ver_rival') {
        const { data: draftRival } = await supabase.from('drafts').select('confirmado').eq('user_id', rivalId).maybeSingle();
        if (draftRival?.confirmado) {
          mostrarToast('Este jugador ya confirmó su equipo. No podés aplicarle este comodín.', 'error');
          setComodinActivo(null);
          return;
        }
      }

      const { data: fechaActiva } = await supabase
        .from('torneo_fechas').select('id, numero_fecha')
        .eq('torneo_id', id).eq('activa', true)
        .order('numero_fecha', { ascending: true }).limit(1).maybeSingle();
      const jornadaNum = fechaActiva?.numero_fecha ?? 1;

      // Leer nickname real del perfil
      const { data: miPerfil } = await supabase.from('perfiles').select('nickname').eq('id', session.user.id).single();
      const miNombre = miPerfil?.nickname || session.user.email.split('@')[0];

      const { usarComodin, verificarLimiteComodin } = await import('../../Tablero/tableroLogica');

      // ── Verificar límite: 2 negativos y 2 positivos por fecha ──
      const NEGATIVOS = ['sabotaje','sabotaje_plus','sabotaje_plusplus','cambiar_restriccion','anular_restriccion','restriccion_extra_lider'];
      const POSITIVOS = ['dinero_extra','dinero_extra_plus','dinero_extra_plusplus']; // ver_rival no tiene límite
      const esNegativo = NEGATIVOS.includes(tipo);
      const esPositivo = POSITIVOS.includes(tipo);
      if ((esNegativo || esPositivo) && rivalId && fechaActiva?.id) {
        const { puede, motivo } = await verificarLimiteComodin(id, rivalId, fechaActiva.id, tipo);
        if (!puede) {
          mostrarToast(`⛔ ${motivo}`, 'error');
          setComodinActivo(null);
          return;
        }
      }

      // Helper para parsear presupuesto "$70M" o número
      const parsarPres = (p) => {
        if (!p) return 0;
        if (typeof p === 'number') return p;
        const n = parseFloat(String(p).replace(/[^0-9.]/g, ''));
        return String(p).includes('M') ? n * 1_000_000 : n;
      };

      // ── Efecto según tipo ──
      if (tipo.startsWith('dinero_extra')) {
        const monto = comodin.tipo?.monto || 1_000_000;
        const { data: miDraft } = await supabase.from('drafts').select('efectos_comodines')
          .eq('user_id', session.user.id).eq('jornada', jornadaNum).maybeSingle();
        const efectos = miDraft?.efectos_comodines || [];
        efectos.push({ tipo, emoji: '', texto: `+$${(monto / 1_000_000).toFixed(1)}M de presupuesto`, monto });
        await supabase.from('drafts').upsert({ user_id: session.user.id, jornada: jornadaNum, efectos_comodines: efectos }, { onConflict: 'user_id,jornada' });

      } else if (tipo.startsWith('sabotaje')) {
        const monto = comodin.tipo?.monto || 1_000_000;
        const { data: draftRival } = await supabase.from('drafts').select('efectos_comodines')
          .eq('user_id', rivalId).eq('jornada', jornadaNum).maybeSingle();
        const efectos = draftRival?.efectos_comodines || [];
        efectos.push({ tipo, emoji: '📉', texto: `${miNombre} te quitó $${(monto / 1_000_000).toFixed(1)}M`, monto: -monto });
        await supabase.from('drafts').upsert({ user_id: rivalId, jornada: jornadaNum, efectos_comodines: efectos }, { onConflict: 'user_id,jornada' });
        // ⚠️ NO BORRAR: notificación para el retado
        await supabase.from('notificaciones').insert({ user_id: rivalId, tipo: 'sabotaje', emoji: '📉', texto: `${miNombre} te saboteó: -$${(monto / 1_000_000).toFixed(1)}M de presupuesto` });

      } else if (tipo === 'ver_rival') {
        const { data: draft } = await supabase.from('drafts')
          .select('equipo, formacion, capitan_index, restricciones, presupuesto')
          .eq('user_id', rivalId).eq('jornada', jornadaNum).maybeSingle();
        if (!draft?.equipo || Object.keys(draft.equipo).length === 0) {
          mostrarToast('Este jugador todavía no armó su equipo', 'error');
          setComodinActivo(null);
          setCargandoComodin(false);
          return;
        }
        setEquipoRival({
          nombre: participantes.find(p => p.user_id === rivalId)?.nombre_usuario,
          equipo: draft.equipo,
          formacion: draft.formacion,
          capitan_index: draft.capitan_index,
          restricciones: draft.restricciones || [],
          presupuesto: draft.presupuesto,
        });
        await usarComodin(comodin.id, fechaActiva?.id, rivalId);
        setComodinActivo(null);
        await fetchTodo(true);
        window.dispatchEvent(new Event('comodin_aplicado'));
        return;

      } else if (tipo === 'cambiar_restriccion') {
        const { data: miDraft } = await supabase.from('drafts').select('ruleta_girada, restricciones')
          .eq('user_id', session.user.id).eq('jornada', jornadaNum).maybeSingle();
        if (!miDraft?.ruleta_girada) {
          mostrarToast('🎡 Todavía no giraste las restricciones esta fecha.', 'error');
          setComodinActivo(null);
          return;
        }
        setComodinCambioRestr({ comodin, restricciones: miDraft.restricciones || [] });
        setComodinActivo(null);
        return;

      } else if (tipo === 'anular_restriccion') {
        const { data: miDraft } = await supabase.from('drafts').select('restricciones, efectos_comodines')
          .eq('user_id', session.user.id).eq('jornada', jornadaNum).maybeSingle();
        const restriccionesOrig = miDraft?.restricciones || [];
        const efectos           = [...(miDraft?.efectos_comodines || [])];
        const idxAnular         = restriccionesOrig.findIndex(r => !r.anulada);
        if (idxAnular < 0) { mostrarToast('No hay restricciones para anular', 'error'); setCargandoComodin(false); return; }
        const restricciones = restriccionesOrig.map((r, i) =>
          i === idxAnular ? { ...r, anulada: true } : r
        );
        efectos.push({ tipo, emoji: '❌', texto: `Restricción "${restriccionesOrig[idxAnular]?.texto}" anulada` });
        await supabase.from('drafts').update({ restricciones, efectos_comodines: efectos })
          .eq('user_id', session.user.id).eq('jornada', jornadaNum);

      } else if (tipo === 'restriccion_extra_lider') {
        const nuevaRestr = { texto: restriccionAleatoria(), cumplida: false, esExtra: true, emoji: '😈' };
        const { data: draftRival } = await supabase.from('drafts').select('restricciones, efectos_comodines')
          .eq('user_id', rivalId).eq('jornada', jornadaNum).maybeSingle();
        const efectos = draftRival?.efectos_comodines || [];
        efectos.push({ tipo, emoji: '😈', texto: `${miNombre} te agregó una restricción extra: "${nuevaRestr.texto}"` });
        await supabase.from('drafts').update({
          restricciones:    [...(draftRival?.restricciones || []), nuevaRestr],
          efectos_comodines: efectos,
        }).eq('user_id', rivalId).eq('jornada', jornadaNum);
        // ⚠️ NO BORRAR: notificación para el retado
        await supabase.from('notificaciones').insert({ user_id: rivalId, tipo: 'restriccion_extra', emoji: '😈', texto: `${miNombre} te agregó una restricción extra: "${nuevaRestr.texto}"` });
      }

      await usarComodin(comodin.id, fechaActiva?.id, rivalId);
      setComodinActivo(null);
      setMenuComodines(false);
      await fetchTodo(true);
        window.dispatchEvent(new Event('comodin_aplicado'));
      mostrarToast('✓ Comodín aplicado');
    } catch (e) {
      console.error(e);
      mostrarToast('Error al usar el comodín: ' + e.message, 'error');
    } finally {
      setCargandoComodin(false);
    }
  };

  // ── Dado ──────────────────────────────────────────────────────────────────

  const tirarDado = async () => {
    if (dadoTirado || dadoAnimando) return;
    setDadoAnimando(true);
    setDadoValor(1);

    let t = 0;
    const iv = setInterval(() => {
      setDadoValor(Math.floor(Math.random() * 12) + 1);
      t += 120;
      if (t >= 1800) {
        clearInterval(iv);
        const resultado = Math.floor(Math.random() * 12) + 1;
        setDadoValor(resultado);
        setDadoAnimando(false);
        setDadoTirado(true);

        // Calcular path de casilleros sin mover todavía
        const miParticipante = participantes.find(p => p.user_id === session.user.id);
        const posActual = miParticipante?.casillero || 0;
        const fechaActiva = fechas.find(f => f.activa && !f.cerrada);

        import('../../Tablero/tableroLogica').then(({ calcularPathCasilleros }) => {
          const path = calcularPathCasilleros(posActual, resultado);
          const destino = path[path.length - 1];
          setPendienteMovimiento({
            resultado,
            pathCasilleros: path,
            casilleroFinal: destino,
            fechaActiva,
          });
        });
      }
    }, 120);
  };

  // ── Confirmar movimiento (click en casillero final) ────────────────────────

  const confirmarMovimiento = async () => {
    if (!pendienteMovimiento || movimientoEnCurso) return;
    setMovimientoEnCurso(true);

    const { resultado, casilleroFinal, fechaActiva } = pendienteMovimiento;

    try {
      const { avanzarEnTablero } = await import('../../Tablero/tableroLogica');
      const res = await avanzarEnTablero(id, session.user.id, resultado);

      if (fechaActiva) {
        await supabase.from('torneo_participantes')
          .update({ dado_tirado_fecha: fechaActiva.numero_fecha })
          .eq('torneo_id', id).eq('user_id', session.user.id);
      }

      // Actualizar posición localmente sin setLoading
      if (res) {
        setParticipantes(prev => prev.map(p =>
          p.user_id === session.user.id
            ? { ...p, casillero: res.casilleroFinal }
            : p
        ));
        // ⚠️ NO BORRAR: mostrar efectos del tablero al usuario
        if (res.efectos && res.efectos.length > 0) {
          setEfectosMovimiento(res);
        }
      }

      // Limpiar estado de path
      setPendienteMovimiento(null);

      // Recargar datos en background sin pantalla de carga
      const { data: p } = await supabase.from('torneo_participantes').select('*').eq('torneo_id', id);
      if (p) setParticipantes(p);
      // Refrescar comodines por si el casillero otorgó uno
      try { setComodines(await fetchComodines(id, session.user.id)); } catch {}

    } catch (err) {
      console.error(err);
      alert('Error al mover la ficha: ' + err.message);
    } finally {
      setMovimientoEnCurso(false);
    }
  };

  // ── Guards ────────────────────────────────────────────────────────────────

  if (loading) return <div style={{ padding: '40px', color: '#aaa' }}>Cargando...</div>;
  if (!torneo) return <div style={{ padding: '40px', color: '#ff4444' }}>Torneo no encontrado</div>;

  const tipoColor = torneo.tipo === 'tactico' ? '#00f2ff' : '#a855f7';
  const tabla     = calcularTabla();
  const colorPosicion = (pos) => ['#f59e0b', '#94a3b8', '#b45309'][pos - 1] || '#64748b';

  // Verificar si el usuario es participante inscripto
  const esMiembro = participantes.some(p => p.user_id === session.user.id);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
    <div style={{
      padding: '10px 20px', color: 'white',
      display: 'flex', gap: '16px', flexWrap: 'wrap',
      justifyContent: 'center',
      minHeight: 'calc(100vh - 46px)', boxSizing: 'border-box',
    }}>

      {/* Banner si no está inscripto */}
      {!esMiembro && (
        <div style={{ position: 'fixed', top: '50px', left: 0, right: 0, zIndex: 9999, background: '#0f172a', borderBottom: '2px solid #f59e0b', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '1.1rem' }}>⚠️</span>
          <p style={{ margin: 0, color: '#f59e0b', fontSize: '0.85rem', fontWeight: 'bold' }}>
            Solo estás viendo este torneo — no estás inscripto. Andá al clan para unirte.
          </p>
        </div>
      )}

      {/* ── COLUMNA IZQUIERDA ── */}
      <div style={{ width: torneo.tipo === 'libre' ? 'min(820px, 100%)' : 'min(440px, 100%)', maxWidth: '100%', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>

        {/* Navegador de fechas */}
        <div style={{ display: 'flex', gap: '8px' }}>

          {/* Botón General */}
          <button
            onClick={() => setFechaVista('general')}
            style={{
              flex: 1, background: fechaVista === 'general' ? `${tipoColor}22` : '#0a0f19',
              border: `1px solid ${fechaVista === 'general' ? tipoColor : '#1e293b'}`,
              borderRadius: '8px', padding: '6px 16px', cursor: 'pointer', textAlign: 'left', transition: '0.2s',
            }}
          >
            <p style={{ color: '#64748b', fontSize: '0.7rem', margin: 0 }}>VISTA</p>
            <p style={{ color: fechaVista === 'general' ? tipoColor : '#fff', fontWeight: 'bold', fontSize: '1rem', margin: '2px 0 0' }}>General</p>
          </button>

          {/* Flechas + fecha actual */}
          {(() => {
            const idxActual    = fechas.findIndex(f => f.id === fechaVista);
            const puedeAtras   = fechaVista !== 'general' && idxActual > 0;
            const puedeDelante = fechaVista !== 'general' && idxActual < fechas.length - 1;
            const fechaObj     = fechas.find(f => f.id === fechaVista);
            const fechaActual  = fechas.find(f => f.activa && !f.cerrada) || fechas[fechas.length - 1];

            if (fechaVista === 'general') {
              return (
                <button
                  onClick={() => fechaActual && setFechaVista(fechaActual.id)}
                  disabled={!fechaActual}
                  style={{
                    flex: 1, background: '#0a0f19', border: '1px solid #1e293b',
                    borderRadius: '8px', padding: '6px 10px', cursor: fechaActual ? 'pointer' : 'default',
                    textAlign: 'left', transition: '0.2s',
                  }}
                  onMouseEnter={e => { if (fechaActual) e.currentTarget.style.borderColor = tipoColor; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e293b'; }}
                >
                  <p style={{ color: '#64748b', fontSize: '0.6rem', margin: 0, textTransform: 'uppercase' }}>Ver fecha</p>
                  <p style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: '1rem', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {fechaActual ? (fechaActual.nombre || `Fecha ${fechaActual.numero_fecha}`) : '—'}
                  </p>
                </button>
              );
            }

            return (
              <div style={{ flex: 1, display: 'flex', gap: '4px' }}>
                <button
                  onClick={() => puedeAtras && setFechaVista(fechas[idxActual - 1].id)}
                  disabled={!puedeAtras}
                  style={{ background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '8px', padding: '0 10px', color: puedeAtras ? '#e2e8f0' : '#1e293b', cursor: puedeAtras ? 'pointer' : 'default', fontSize: '1rem' }}
                >‹</button>

                <div style={{
                  flex: 1, background: `${tipoColor}22`,
                  border: `1px solid ${tipoColor}`,
                  borderRadius: '8px', padding: '6px 10px', textAlign: 'left',
                }}>
                  <span style={{ color: fechaObj?.activa && !fechaObj?.cerrada ? '#22c55e' : '#475569', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {fechaObj?.activa && !fechaObj?.cerrada ? 'en curso' : 'finalizada'}
                  </span>
                  <p style={{ color: tipoColor, fontWeight: 'bold', fontSize: '1rem', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {fechaObj?.nombre || `Fecha ${fechaObj?.numero_fecha}`}
                  </p>
                </div>

                <button
                  onClick={() => puedeDelante && setFechaVista(fechas[idxActual + 1].id)}
                  disabled={!puedeDelante}
                  style={{ background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '8px', padding: '0 10px', color: puedeDelante ? '#e2e8f0' : '#1e293b', cursor: puedeDelante ? 'pointer' : 'default', fontSize: '1rem' }}
                >›</button>
              </div>
            );
          })()}
        </div>

        {/* Tabla de posiciones siempre visible */}

        {/* Tabla de posiciones */}
        <div style={{ background: '#0a0f19', borderRadius: '12px', overflow: 'hidden', border: '1px solid #1e293b', flex: '0 0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 90px 70px', padding: '8px 16px', borderBottom: '1px solid #1e293b' }}>
            {['#', 'JUGADOR', 'GANÓ FECHA', 'PUNTOS'].map(col => (
              <span key={col} style={{ color: '#64748b', fontSize: '0.72rem', textAlign: col === 'PUNTOS' ? 'right' : col === 'GANÓ FECHA' ? 'center' : 'left' }}>{col}</span>
            ))}
          </div>

          {tabla.map((p, idx) => {
            const pos         = idx + 1;
            const esMio       = p.user_id === session.user.id;
            const idxOriginal = participantes.findIndex(part => part.user_id === p.user_id);
            const colorFicha  = COLORES_FICHAS[idxOriginal % COLORES_FICHAS.length];
            const ptFecha     = fechaVista !== 'general' ? puntos.find(pt => pt.user_id === p.user_id && pt.fecha_id === fechaVista) : null;
            const fechaObj    = fechas.find(f => f.id === fechaVista);
            const fechaActiva = fechaObj && fechaObj.activa && !fechaObj.cerrada;
            const tieneDatos  = ptFecha?.equipo && Object.keys(ptFecha.equipo).length > 0;
            const draftP      = draftsParticipantes[p.user_id];
            const restricciones = draftP?.restricciones?.filter(r => !r.anulada) || [];

            return (
              <div key={p.id} style={{
                display: 'grid', gridTemplateColumns: '48px 1fr 90px 70px',
                padding: '12px 16px',
                borderBottom: idx < tabla.length - 1 ? '1px solid #1e293b' : 'none',
                background: esMio ? `${tipoColor}0d` : 'transparent',
              }}>
                <span style={{ color: colorPosicion(pos), fontWeight: 'bold', fontSize: '1rem' }}>
                  {pos === 1 ? '🥇' : pos === 2 ? '🥈' : pos === 3 ? '🥉' : `#${pos}`}
                </span>

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: colorFicha, flexShrink: 0 }} />
                  <span
                    onClick={() => navigate(`/palmares/${p.user_id}`)}
                    style={{ color: esMio ? tipoColor : '#e2e8f0', fontWeight: esMio ? 'bold' : 'normal', fontSize: '0.9rem', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                    onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                    onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                  >
                    {clanTags[p.user_id] ? `[${clanTags[p.user_id]}] ` : ''}{p.nombre_usuario}
                  </span>
                  {esMio && <span style={{ color: tipoColor, fontSize: '0.6rem', border: `1px solid ${tipoColor}`, padding: '1px 4px', borderRadius: '10px', flexShrink: 0 }}>vos</span>}
                  {p.user_id === torneo.creador_id && <span style={{ color: '#f59e0b', fontSize: '0.7rem', flexShrink: 0 }}>★</span>}

                  {/* Botón ver equipo/restricciones por fecha */}
                  {fechaVista !== 'general' && (
                    fechaActiva ? (
                      torneo.tipo === 'tactico' && restricciones.length > 0 && (
                        <button
                          onClick={() => setEquipoDetalle({ nombre: p.nombre_usuario, formacion: draftP?.formacion, presupuesto: draftP?.presupuesto, soloRestricciones: true, restricciones, equipo: draftP?.equipo || {}, capitan_index: draftP?.capitan_index ?? null  })}
                          style={{ background: '#f59e0b22', border: '1px solid #f59e0b44', borderRadius: '4px', padding: '2px 6px', color: '#f59e0b', fontSize: '0.6rem', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap', lineHeight: 1.2 }}
                        >
                          restricciones
                        </button>
                      )
                    ) : (
                      tieneDatos && (
                        <button
                          onClick={() => setEquipoDetalle({ nombre: p.nombre_usuario, equipo: ptFecha.equipo, formacion: ptFecha.formacion, restricciones: ptFecha.restricciones, puntos: ptFecha.puntos, capitan_index: ptFecha.capitan_index ?? draftP?.capitan_index ?? null, puntos_por_jugador: ptFecha.puntos_por_jugador ?? null, jornada_numero: ptFecha.fecha?.numero_fecha ?? null })}
                          style={{ background: `${tipoColor}22`, border: `1px solid ${tipoColor}44`, borderRadius: '4px', padding: '2px 6px', color: tipoColor, fontSize: '0.6rem', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap', lineHeight: 1.2 }}
                        >
                          ver equipo
                        </button>
                      )
                    )
                  )}
                </div>

                <span style={{ textAlign: 'center', color: p.fechasGanadas > 0 ? '#f59e0b' : '#334155', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {p.fechasGanadas > 0 ? `${p.fechasGanadas}x ` : '—'}
                </span>
                <span style={{ textAlign: 'right', fontWeight: 'bold', color: p.puntosTotal > 0 ? '#e2e8f0' : '#334155' }}>
                  {p.puntosTotal}
                </span>
              </div>
            );
          })}
        </div>

        {/* Botón Ir a mi equipo */}
        <button
          onClick={() => navigate(torneo.tipo === 'tactico' ? '/equipo-tactico' : '/equipo-libre')}
          style={{
            width: '100%', background: 'transparent',
            border: `1px solid ${tipoColor}55`,
            borderRadius: '8px', padding: '10px 16px',
            cursor: 'pointer', textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: '10px',
            transition: '0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = tipoColor}
          onMouseLeave={e => e.currentTarget.style.borderColor = `${tipoColor}55`}
        >
          <span style={{ fontSize: '1rem' }}>{torneo.tipo === 'tactico' ? '' : ''}</span>
          <div>
            <p style={{ color: '#64748b', fontSize: '0.65rem', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>MI EQUIPO</p>
            <p style={{ color: tipoColor, fontWeight: 'bold', fontSize: '0.9rem', margin: '2px 0 0' }}>
              Ir a {torneo.tipo === 'tactico' ? 'Equipo Táctico' : 'Equipo Libre'} →
            </p>
          </div>
        </button>

        {/* Panel de comodines (solo táctico) */}
        {torneo.tipo === 'tactico' && (
          <div>
            <button
              onClick={() => setMenuComodines(v => !v)}
              style={{
                width: '100%', background: menuComodines ? `${tipoColor}22` : '#0a0f19',
                border: `1px solid ${menuComodines ? tipoColor : '#1e293b'}`,
                borderRadius: '8px', padding: '10px 16px', cursor: 'pointer', textAlign: 'left', transition: '0.2s',
              }}
            >
              <p style={{ color: '#64748b', fontSize: '0.7rem', margin: 0 }}>COMODINES</p>
              <p style={{ color: menuComodines ? tipoColor : '#fff', fontWeight: 'bold', fontSize: '1.2rem', margin: '4px 0 0', display: 'flex', alignItems: 'center' }}>
                {cargandoComodin ? <span style={{ fontSize: '0.8rem', color: '#f59e0b' }}>Aplicando...</span> : comodines.length}
                <span style={{ fontSize: '0.7rem', color: '#64748b', marginLeft: 'auto' }}>{menuComodines ? '▲' : '▼'}</span>
              </p>
            </button>

            {menuComodines && (
              <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)' }}
                onClick={() => { setMenuComodines(false); setComodinActivo(null); }}>
              <div style={{
                background: '#0f172a', border: `1px solid ${tipoColor}66`,
                borderRadius: '14px', padding: '16px', width: '380px', maxWidth: '95vw',
                boxShadow: '0 0 40px rgba(0,0,0,0.8)', maxHeight: '80vh', overflowY: 'auto',
              }} onClick={e => e.stopPropagation()}>
                {/* Selector de rival si hay comodín activo */}
                {comodinActivo ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <span>{comodinActivo.tipo?.emoji}</span>
                      <p style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 'bold', margin: 0 }}>
                        {comodinActivo.tipo?.nombre} — elegí un rival
                      </p>
                    </div>
                    {participantes.filter(p => p.user_id !== session.user.id).map(p => (
                      <button key={p.user_id}
                        onClick={() => esMiembro ? ejecutarComodin(comodinActivo, p.user_id) : null} 
                        disabled={cargandoComodin || !esMiembro}
                        style={{ width: '100%', background: cargandoComodin ? '#1e293b' : '#0a0f19', border: '1px solid #1e293b', borderRadius: '8px', padding: '8px 12px', color: cargandoComodin ? '#64748b' : '#e2e8f0', cursor: cargandoComodin ? 'wait' : 'pointer', textAlign: 'left', marginBottom: '4px', fontSize: '0.85rem', transition: '0.2s' }}
                        onMouseEnter={e => (e.currentTarget.style.borderColor = tipoColor)}
                        onMouseLeave={e => (e.currentTarget.style.borderColor = '#1e293b')}
                      >
                        {clanTags[p.user_id] ? `[${clanTags[p.user_id]}] ` : ''}{p.nombre_usuario}
                      </button>
                    ))}
                    <button onClick={() => setComodinActivo(null)}
                      style={{ width: '100%', background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '6px', fontSize: '0.8rem', marginTop: '4px' }}>
                      Cancelar
                    </button>
                  </div>
                ) : comodines.length === 0 ? (
                  <p style={{ color: '#334155', fontSize: '0.8rem', textAlign: 'center', margin: '12px 0' }}>No tenés comodines disponibles</p>
                ) : (
                  comodines.map(c => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#0a0f19', borderRadius: '8px', padding: '10px 12px', border: '1px solid #1e293b', marginBottom: '6px' }}>
                      <span style={{ fontSize: '1.1rem' }}>{c.tipo?.emoji || '🃏'}</span>
                      <div style={{ flex: 1 }}>
                        <p style={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 'bold', margin: 0 }}>{c.tipo?.nombre}</p>
                        <p style={{ color: '#64748b', fontSize: '0.72rem', margin: '2px 0 0' }}>{c.tipo?.descripcion}</p>
                        <p style={{ color: '#475569', fontSize: '0.65rem', margin: '3px 0 0', fontStyle: 'italic' }}> El efecto se aplica en la próxima fecha</p>
                      </div>
                      <button onClick={() => ejecutarComodin(c)} disabled={cargandoComodin}
                        style={{ background: cargandoComodin ? '#334155' : tipoColor, color: '#000', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '0.72rem', fontWeight: 'bold', cursor: cargandoComodin ? 'wait' : 'pointer', minWidth: '42px' }}>
                        {cargandoComodin ? '...' : 'Usar'}
                      </button>
                    </div>
                  ))
                )}
              </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── COLUMNA DERECHA (solo táctico, oculta en mobile) ── */}
      {torneo.tipo === 'tactico' && typeof window !== 'undefined' && window.innerWidth > 600 && <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px', overflow: 'hidden' }}>

        {/* Info del torneo */}
        <div style={{ background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '8px', padding: '6px 16px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1rem' }}>{torneo.nombre}</h2>
            <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b' }}>
              {torneo.tipo === 'tactico' ? 'Táctico' : 'Libre'} · Código: <span style={{ color: tipoColor, fontFamily: 'monospace' }}>{torneo.codigo_invitacion}</span>
            </p>
          </div>

        </div>

        {/* Tablero + dado (solo táctico, oculto en mobile) */}
        {torneo.tipo === 'tactico' && typeof window !== 'undefined' && window.innerWidth > 600 && (
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ width: '100%', position: 'relative' }}>

              {/* Overlay SOLO durante la animación del dado (no después) */}
              {dadoAnimando && (
                <div style={{
                  position: 'absolute', inset: 0, zIndex: 5,
                  background: 'rgba(5,8,16,0.6)', borderRadius: '10px',
                  backdropFilter: 'blur(1px)',
                  pointerEvents: 'none',
                }} />
              )}

              <Tablero
                participantes={participantes}
                posiciones={Object.fromEntries(participantes.map(p => [p.user_id, p.casillero || 0]))}
                configCasilleros={configCasilleros}
                miUserId={dadoAnimando ? session.user.id : null}
                pathCasilleros={pendienteMovimiento?.pathCasilleros ?? null}
                casilleroFinal={pendienteMovimiento?.casilleroFinal ?? null}
                onCasilleroClick={pendienteMovimiento ? confirmarMovimiento : null}
              />

              {/* Dado d12 estilo D&D */}
              {(() => {
                // D12: dodecágono (12 lados) con pentágono interior — aspecto auténtico de dado de rol
                const CX = 60, CY = 60, R_EXT = 52, R_INT = 28;
                const CARAS = 12;

                // Vértices del polígono exterior (dodecágono)
                const ptsExt = Array.from({ length: CARAS }, (_, i) => {
                  const ang = (i * 2 * Math.PI / CARAS) - Math.PI / 2;
                  return [CX + R_EXT * Math.cos(ang), CY + R_EXT * Math.sin(ang)];
                });

                // Vértices del pentágono interior (cara visible del d12)
                const ptsInt = Array.from({ length: 5 }, (_, i) => {
                  const ang = (i * 2 * Math.PI / 5) - Math.PI / 2;
                  return [CX + R_INT * Math.cos(ang), CY + R_INT * Math.sin(ang)];
                });

                const toPath = (pts) => pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(2)} ${p[1].toFixed(2)}`).join(' ') + ' Z';

                // Líneas desde cada vértice del dodecágono al pentágono más cercano
                const lineas = ptsExt.map((pe, i) => {
                  const pi = ptsInt[Math.round(i * 5 / CARAS) % 5];
                  return { x1: pe[0], y1: pe[1], x2: pi[0], y2: pi[1] };
                });

                const strokeColor = dadoTirado ? '#1e3a5a' : (esMiembro ? '#00f2ff' : '#334155');
                const fillColor   = dadoAnimando ? '#0d1f33' : '#0a1828';
                const glowFilter  = !esMiembro
                  ? 'none'
                  : dadoAnimando
                    ? 'drop-shadow(0 0 14px #00f2ffcc) drop-shadow(0 0 4px #00f2ff)'
                    : dadoTirado
                      ? 'drop-shadow(0 0 5px #00f2ff33)'
                      : 'drop-shadow(0 0 12px #00f2ff99) drop-shadow(0 0 24px #00f2ff44)';

                return (
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', zIndex: 10,
                  }}>
                    <style>{`
                      @keyframes d12-spin   { 0%{transform:rotate(0deg) scale(1)} 40%{transform:rotate(200deg) scale(0.82)} 70%{transform:rotate(320deg) scale(1.05)} 100%{transform:rotate(360deg) scale(1)} }
                      @keyframes d12-idle   { 0%,100%{transform:translateY(0px) rotate(-3deg) scale(1)} 50%{transform:translateY(-6px) rotate(3deg) scale(1.03)} }
                      @keyframes d12-glow   { 0%,100%{filter:drop-shadow(0 0 10px #00f2ff88) drop-shadow(0 0 20px #00f2ff33)} 50%{filter:drop-shadow(0 0 20px #00f2ffcc) drop-shadow(0 0 36px #00f2ff66)} }
                      @keyframes d12-appear { 0%{transform:scale(0.4) rotate(-30deg);opacity:0} 55%{transform:scale(1.12) rotate(6deg);opacity:1} 100%{transform:scale(1) rotate(0deg);opacity:1} }
                      @keyframes d12-pulse-int { 0%,100%{opacity:0.5} 50%{opacity:1} }
                      .d12-interior { animation: d12-pulse-int 2s ease-in-out infinite; }
                    `}</style>

                    <svg
                      width="120" height="120"
                      viewBox="0 0 120 120"
                      style={{
                        cursor: !dadoTirado && esMiembro ? 'pointer' : 'default',
                        opacity: !esMiembro ? 0.3 : 1,
                        animation: !esMiembro ? 'none'
                          : dadoAnimando ? 'd12-spin 0.45s ease-in-out infinite'
                          : dadoTirado ? 'd12-appear 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards'
                          : 'd12-idle 3s ease-in-out infinite, d12-glow 2.5s ease-in-out infinite',
                        filter: glowFilter,
                      }}
                      onClick={esMiembro ? tirarDado : undefined}
                    >
                      {/* Sombra exterior */}
                      <polygon
                        points={ptsExt.map(p => `${(p[0]+2).toFixed(1)},${(p[1]+3).toFixed(1)}`).join(' ')}
                        fill="rgba(0,0,0,0.5)"
                      />

                      {/* Cuerpo principal del dodecágono */}
                      <polygon
                        points={ptsExt.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')}
                        fill={fillColor}
                        stroke={strokeColor}
                        strokeWidth={dadoTirado ? '1.5' : '2'}
                        strokeLinejoin="round"
                      />

                      {/* Líneas internas (aristas del d12) */}
                      {lineas.map((l, i) => (
                        <line
                          key={i}
                          x1={l.x1.toFixed(1)} y1={l.y1.toFixed(1)}
                          x2={l.x2.toFixed(1)} y2={l.y2.toFixed(1)}
                          stroke={strokeColor}
                          strokeWidth={dadoTirado ? '0.6' : '0.9'}
                          opacity={dadoAnimando ? '0.4' : '0.6'}
                        />
                      ))}

                      {/* Pentágono central (cara visible) */}
                      <path
                        d={toPath(ptsInt)}
                        fill="none"
                        stroke={strokeColor}
                        strokeWidth={dadoTirado ? '1' : '1.4'}
                        opacity={dadoAnimando ? '0.5' : '0.85'}
                        className={dadoTirado ? '' : 'd12-interior'}
                        strokeLinejoin="round"
                      />

                      {/* Brillo superior izquierdo */}
                      <polygon
                        points={ptsExt.slice(0, 4).map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')}
                        fill="rgba(255,255,255,0.04)"
                      />

                      {/* Número en el centro */}
                      <text
                        x={CX} y={CY + 1}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={dadoValor && dadoValor >= 10 ? '22' : '26'}
                        fontWeight="900"
                        fontFamily="Arial Black, Impact, sans-serif"
                        fill={dadoTirado ? '#1e3a5a' : dadoAnimando ? '#00f2ff88' : '#00f2ff'}
                        stroke={dadoTirado ? 'none' : 'rgba(0,20,40,0.8)'}
                        strokeWidth="3"
                        paintOrder="stroke"
                        letterSpacing="-1"
                      >
                        {dadoValor ?? '?'}
                      </text>

                      {/* Label "d12" en el borde inferior */}
                      <text
                        x={CX} y={CY + R_EXT - 6}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="7"
                        fontWeight="bold"
                        fontFamily="monospace"
                        fill={strokeColor}
                        opacity="0.5"
                        letterSpacing="1"
                      >
                        d12
                      </text>
                    </svg>

                    <div style={{ textAlign: 'center' }}>
                      {dadoValor && !dadoAnimando && (
                        <div style={{
                          color: '#00f2ff', fontSize: '1rem', fontWeight: 'bold',
                          textShadow: '0 0 12px #00f2ff', marginBottom: '4px',
                          animation: 'd12-appear 0.5s cubic-bezier(0.34,1.56,0.64,1)',
                        }}>
                          +{dadoValor} casillero{dadoValor > 1 ? 's' : ''}
                        </div>
                      )}
                      <span style={{
                        fontSize: '0.62rem', fontWeight: 'bold', letterSpacing: '1px',
                        textTransform: 'uppercase', padding: '3px 10px', borderRadius: '4px',
                        background: pendienteMovimiento ? '#00ff8818'
                          : dadoTirado ? '#0f172a' : '#00f2ff18',
                        color: pendienteMovimiento ? '#00ff88'
                          : dadoTirado ? '#334155' : '#00f2ff',
                        border: `1px solid ${pendienteMovimiento ? '#00ff8844'
                          : dadoTirado ? '#1e293b' : '#00f2ff44'}`,
                        animation: pendienteMovimiento ? 'none' : 'none',
                      }}>
                        {dadoAnimando ? 'Tirando...'
                          : pendienteMovimiento ? `¡Hacé click en el casillero ${pendienteMovimiento.casilleroFinal}!`
                          : dadoTirado ? 'Ya tiraste esta fecha'
                          : 'Clic para tirar'}
                      </span>
                    </div>
                  </div>
                );
              })()}

            </div>
          </div>
        )}
      </div>}
    </div>

    {/* ── MODAL: Ver equipo rival ── */}
    {/* ── MODAL: Ver equipo (unificado) ── */}
    {(equipoRival || equipoDetalle) && (
      <ModalVerEquipo
        datos={equipoRival
          ? { nombre: equipoRival.nombre, equipo: equipoRival.equipo || {}, formacion: equipoRival.formacion, capitan_index: equipoRival.capitan_index ?? null }
          : equipoDetalle
        }
        onClose={() => { setEquipoRival(null); setEquipoDetalle(null); }}
        tipoColor={tipoColor}
        getJugadoresRelevantes={getJugadoresRelevantes}
      />
    )}

    {/* ── MODAL: Cambiar restricción ── */}
    {comodinCambioRestr && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#0f172a', border: `2px solid ${tipoColor}`, borderRadius: '16px', padding: '28px 32px', maxWidth: '420px', width: '90%', textAlign: 'center' }}>
          <p style={{ fontSize: '1.5rem', margin: '0 0 8px' }}>🔄</p>
          <h3 style={{ color: tipoColor, margin: '0 0 8px', letterSpacing: '1px' }}>CAMBIAR RESTRICCIÓN</h3>

          {!previewCambio ? (
            <>
              <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0 0 20px' }}>Elegí cuál restricción querés cambiar</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {comodinCambioRestr.restricciones.filter(r => !r.anulada).map((res, i) => (
                  <button key={i}
                    onClick={() => {
                      const disponibles = POOL_RESTRICCIONES.filter(p => p !== res.texto);
                      const nueva       = disponibles[Math.floor(Math.random() * disponibles.length)];
                      setPreviewCambio({ textoViejo: res.texto, textoNuevo: nueva });
                    }}
                    style={{ background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 16px', color: '#e2e8f0', cursor: 'pointer', fontSize: '0.85rem', textAlign: 'left', transition: '0.2s' }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = tipoColor)}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = '#1e293b')}
                  >
                    {res.texto}
                  </button>
                ))}
              </div>
              <button onClick={() => setComodinCambioRestr(null)}
                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.8rem' }}>
                Cancelar (el comodín queda disponible)
              </button>
            </>
          ) : (
            <>
              <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0 0 20px' }}>Tu nueva restricción será:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
                <div style={{ background: '#0a0f19', borderRadius: '8px', padding: '10px 16px', border: '1px solid #ff444466' }}>
                  <p style={{ color: '#64748b', fontSize: '0.7rem', margin: '0 0 2px' }}>ANTES</p>
                  <p style={{ color: '#ff4444', fontSize: '0.9rem', margin: 0, textDecoration: 'line-through' }}>{previewCambio.textoViejo}</p>
                </div>
                <div style={{ color: '#334155', fontSize: '1.2rem' }}>↓</div>
                <div style={{ background: '#0a0f19', borderRadius: '8px', padding: '10px 16px', border: '1px solid #22c55e66' }}>
                  <p style={{ color: '#64748b', fontSize: '0.7rem', margin: '0 0 2px' }}>NUEVA</p>
                  <p style={{ color: '#22c55e', fontSize: '0.9rem', fontWeight: 'bold', margin: 0 }}>{previewCambio.textoNuevo}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={async () => {
                    const { usarComodin } = await import('../../Tablero/tableroLogica');
                    const nuevasRestr = comodinCambioRestr.restricciones.map(r =>
                      r.texto === previewCambio.textoViejo && !r.anulada
                        ? { ...r, texto: previewCambio.textoNuevo, cumplida: false }
                        : r
                    );
                    const { data: fechaActiva } = await supabase.from('torneo_fechas').select('id')
                      .eq('torneo_id', id).eq('cerrada', false).order('numero_fecha', { ascending: false }).limit(1).maybeSingle();
                    await supabase.from('drafts').update({ restricciones: nuevasRestr }).eq('user_id', session.user.id);
                    await usarComodin(comodinCambioRestr.comodin.id, fechaActiva?.id);
                    setComodinCambioRestr(null);
                    setPreviewCambio(null);
                    await fetchTodo(true);
        window.dispatchEvent(new Event('comodin_aplicado'));
                    mostrarToast('✓ Restricción cambiada');
                  }}
                  style={{ flex: 1, background: tipoColor, color: '#000', border: 'none', borderRadius: '8px', padding: '10px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  Confirmar
                </button>
                <button
                  onClick={() => { setComodinCambioRestr(null); setPreviewCambio(null); }}
                  style={{ flex: 1, background: 'transparent', color: '#aaa', border: '1px solid #333', borderRadius: '8px', padding: '10px', cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  Cancelar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )}
      {/* ⚠️ NO BORRAR: Modal de efectos del tablero tras mover */}
      {efectosMovimiento && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 99998, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setEfectosMovimiento(null)}>
          <div style={{ background: '#080d14', border: '2px solid #00f2ff44', borderRadius: '16px', padding: '28px 32px', maxWidth: '420px', width: '90%', textAlign: 'center' }}
            onClick={e => e.stopPropagation()}>
            <p style={{ color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 16px' }}>Resultado del dado</p>
            <p style={{ color: '#e2e8f0', margin: '0 0 20px', fontSize: '0.9rem' }}>
              Casillero <strong style={{ color: '#00f2ff' }}>{efectosMovimiento.casilleroAnterior}</strong> → <strong style={{ color: '#00f2ff' }}>{efectosMovimiento.casilleroFinal}</strong>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {efectosMovimiento.efectos.map((ef, i) => {
                const esNegativo = ['trampa_retroceso','trampa_combo','trampa_pierde_turno'].includes(ef.tipo);
                const esComodin  = ['comodin_aleatorio','comodin_fijo'].includes(ef.tipo);
                const color = esNegativo ? '#ef4444' : esComodin ? '#f59e0b' : '#22c55e';
                const emoji = ef.emoji || (esComodin ? '🎁' : esNegativo ? '⚠️' : '✓');
                let texto = '';
                if (ef.tipo === 'trampa_retroceso')    texto = `Retrocedés ${ef.retroceso} casilleros`;
                else if (ef.tipo === 'trampa_combo')   texto = `${ef.descripcion || `Retrocedés ${ef.retroceso} y perdés $${(ef.monto/1000000).toFixed(1)}M`} — efecto en próxima fecha`;
                else if (ef.tipo === 'trampa_pierde_turno') texto = 'No podés tirar el dado la próxima fecha';
                else if (ef.tipo === 'bonus_avance')   texto = `Avanzás ${ef.bonus} casilleros extra`;
                else if (ef.tipo === 'comodin_aleatorio') texto = '¡Comodín sorpresa añadido a tu inventario!';
                else if (ef.tipo === 'comodin_fijo')   texto = '¡Comodín añadido a tu inventario!';
                else if (ef.tipo === 'vuelta_completa') texto = `¡Vuelta completa! +${ef.vueltas} comodín`;
                else texto = ef.tipo;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px', background: `${color}11`, border: `1px solid ${color}33` }}>
                    <span style={{ fontSize: '1.2rem' }}>{emoji}</span>
                    <span style={{ color, fontSize: '0.85rem', fontWeight: '500', textAlign: 'left' }}>{texto}</span>
                  </div>
                );
              })}
            </div>
            <button onClick={() => setEfectosMovimiento(null)}
              style={{ background: '#00f2ff22', border: '1px solid #00f2ff55', borderRadius: '8px', padding: '9px 28px', color: '#00f2ff', fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer' }}>
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Toast centrado en pantalla */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          background: toast.tipo === 'error' ? '#1a0505' : '#051a0a',
          border: '2px solid ' + (toast.tipo === 'error' ? '#ef4444' : '#22c55e'),
          color: toast.tipo === 'error' ? '#ef4444' : '#22c55e',
          padding: '20px 36px', borderRadius: '14px',
          fontSize: '1rem', fontWeight: 'bold', textAlign: 'center',
          zIndex: 99999,
          boxShadow: '0 0 60px rgba(0,0,0,0.8), 0 0 20px ' + (toast.tipo === 'error' ? '#ef444433' : '#22c55e33'),
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}>
          {toast.msg}
        </div>
      )}
          <Tutorial 
            tutorialId={`torneo_${torneo?.tipo || 'tactico'}`}
            userId={session.user.id} 
            pasos={TUTORIALES[`torneo_${torneo?.tipo || 'tactico'}`] || TUTORIALES.torneos} botonVerDeNuevo={true}
          />
      {/* Tabs sacados — tabla y ranking van en otro lado */}
    </>
  );
};

export default TorneoDetalle;